import { formatDateTime } from '../Order/shared/orderFormat';

// The store's clock. Saudi Arabia has no daylight saving, so the offset never changes.
export const STORE_TIME_ZONE = 'Asia/Riyadh';
const STORE_OFFSET = '+03:00';

/** The history's filter: every status, or one. The server's words, not the dashboard's. */
export const STATUSES = [
  { value: '', label: 'All' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'sent', label: 'Sent' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'failed', label: 'Not sent' },
];

const STATUS_VIEW = {
  scheduled: { label: 'Scheduled', variant: 'info' },
  sent: { label: 'Sent', variant: 'success' },
  cancelled: { label: 'Cancelled', variant: 'muted' },
  failed: { label: 'Not sent', variant: 'danger' },
};

/** The badge for a status: its label and the kit's badge variant. */
export const statusView = (status) => STATUS_VIEW[status] || { label: status || '—', variant: 'outline' };

/** "Everyone" or "3 customers": who the push was written for. */
export const audienceLabel = (audience) => {
  if (!audience || audience.everyone) return 'Everyone';
  const count = Number(audience.customers || 0);
  return `${count} customer${count === 1 ? '' : 's'}`;
};

/** "Reached 120" once sent: the customers who had a device and Offers & news on. */
export const reachedLabel = (row) =>
  row.status === 'sent' && row.recipients != null ? `Reached ${Number(row.recipients).toLocaleString('en-US')}` : '';

/** The time a row is about: when it goes (scheduled), went (sent), or was written. */
export const whenOf = (row) => row.scheduled_for || row.sent_on || row.created_on;

export const whenLabel = (row) => formatDateTime(whenOf(row));

const OPENS = { product: 'product', brand: 'brand', category: 'category', offer: 'offer' };

/** "Byredo (brand)", or "Opens the app" when a tap opens nothing in particular. */
export const opensLabel = (opens) => {
  if (!opens || !opens.screen) return 'Opens the app';
  const kind = OPENS[opens.screen] || opens.screen;
  return opens.label ? `${opens.label} (${kind})` : `${kind} #${opens.screen_id}`;
};

/** A datetime-local value, typed as Riyadh time, as the ISO 8601 time the server expects. */
export const toScheduledIso = (value) => (value ? `${value}:00${STORE_OFFSET}` : '');

/** ``date`` as a datetime-local value in Riyadh time: "2026-11-06T20:00". */
export const toLocalValue = (date) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: STORE_TIME_ZONE,
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(date);
  const part = (type) => parts.find((p) => p.type === type)?.value;
  // Some engines print midnight as "24" with hour12 off.
  const hour = part('hour') === '24' ? '00' : part('hour');
  return `${part('year')}-${part('month')}-${part('day')}T${hour}:${part('minute')}`;
};

/** The earliest time the picker offers: five minutes from now, Riyadh time. */
export const earliestScheduleValue = (now = Date.now()) => toLocalValue(new Date(now + 5 * 60_000));

/** "1–20 of 57": the rows on this page. */
export const rangeLabel = (pagination) => {
  const { page = 1, per_page: perPage = 20, total = 0 } = pagination || {};
  if (!total) return 'No notifications';
  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);
  return `${from}–${to} of ${total}`;
};
